import type { ShipToAddress, WarehouseCountry } from '../ship-to.js';

/**
 * `POST /OutterApi/Api/warehouses` (获取仓库地址) → `ShipToAddress`.
 *
 * The vendor document describes a row as `{ id, name, address }` and nothing else. The live
 * endpoint returns far more than that, and this adapter is written against the live response —
 * confirmed by calling it — because the document's example is an abbreviation, not the contract.
 * Everything mapped below was observed on the wire; nothing here is inferred from the document.
 *
 * Three things about that response shape drive the code:
 *
 *  1. Every value arrives as a string, including ids and day counts. `str` and `days` coerce
 *     rather than trusting a type, because a row that starts sending `id: 1` instead of `"1"`
 *     should not empty the screen.
 *  2. Copy fields are doubled per language — `name_cn`/`name_en`, `remark_cn`/`remark_en`,
 *     `attention_cn`/`attention_en` — and the vendor has no Malay. `language` picks a side and
 *     falls back to the other rather than rendering an empty row.
 *  3. `attention_*` is HTML, stored HTML-escaped, and malformed in places: two of the six rows
 *     open their first line with `&lt;/p&gt;`. It is decoded, split and stripped to plain lines
 *     here so no component is ever handed markup to render.
 *
 * The address itself is deliberately *not* language-resolved. `address_en` exists on the wire and
 * is null on every row today, but even when populated it would be the wrong thing to show: a
 * forwarding address is read by a courier in the origin country, so it stays in its own script
 * whatever language the UI is in. Same reasoning as `ShipToAddress`'s own doc comment.
 */

/**
 * Which side of the vendor's bilingual fields to prefer.
 *
 * Only two values because the vendor only stores two. The app's third locale (ms) maps to `en`
 * at the call site — the honest fallback, since inventing Malay for a vendor notice would put
 * words in their mouth about storage fees and customs.
 */
export type WarehouseLanguage = 'zh' | 'en';

/**
 * Country ids as the vendor numbers them, from `POST /OutterApi/Api/country`: 1 MALAYSIA,
 * 3 CHINA, 4 JAPAN, 9 TAIWAN, 10 THAILAND, 14 KOREA. Those six are every country the warehouse
 * list currently names; an id outside them yields `undefined` rather than a guess, which costs a
 * `lang` attribute and a postcode-first address ordering, not a rendered row.
 */
const COUNTRY_BY_ID: Readonly<Record<string, WarehouseCountry>> = {
  '1': 'MY',
  '3': 'CN',
  '4': 'JP',
  '9': 'TW',
  '10': 'TH',
  '14': 'KR',
};

/**
 * The entities actually seen in this data, plus the handful any CMS-authored text tends to carry.
 *
 * Written out rather than done with a DOM parser because @mypoz/shared runs in Vitest under Node
 * as well as in the browser, and because routing vendor HTML through `innerHTML` to decode it is
 * how an injected `<script>` gets a chance to run.
 */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  minus: '−',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  middot: '·',
};

/**
 * One decoding pass, not repeated to a fixed point.
 *
 * The tags in `attention_*` are escaped once (`&lt;p&gt;`), so one pass is what turns them into
 * tags this module can strip. Decoding repeatedly would also turn a literal `&amp;lt;` — text a
 * warehouse actually meant to write — into a tag, which is the bug that makes escaped text
 * dangerous.
 */
function decodeEntities(value: string): string {
  return value.replace(/&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body.startsWith('#')) {
      const hex = body[1] === 'x' || body[1] === 'X';
      const codePoint = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return match;
      return String.fromCodePoint(codePoint);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A field as text: entity-decoded, whitespace-collapsed, empty for absent/null/non-scalar. */
function str(row: Record<string, unknown>, key: string): string {
  const value = row[key];
  const raw =
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : '';
  return decodeEntities(raw).replace(/\s+/g, ' ').trim();
}

/**
 * A field as a whole number of days, or undefined.
 *
 * Zero is rejected along with negatives and blanks: on this endpoint a `0` day count means the
 * column was never configured, and "free storage for 0 days" is a worse thing to tell a customer
 * than saying nothing.
 */
function days(row: Record<string, unknown>, key: string): number | undefined {
  const raw = str(row, key);
  if (raw === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : undefined;
}

function firstNonEmpty(...values: readonly string[]): string {
  return values.find((value) => value !== '') ?? '';
}

/**
 * `attention_cn` / `attention_en` as plain lines.
 *
 * Split on the paragraph and break tags rather than on newlines alone, because the rows are
 * inconsistent about which they use, and two of them mark a line with a stray closing `</p>`
 * where an opening one belongs. Whatever tags survive that are stripped outright: these lines are
 * rendered as text, never as markup.
 *
 * The vendor numbers its own lines ("1. …", "2. …") inside the text, so the caller must not add
 * list numbering of its own — see WarehouseSection.
 */
function toNotices(value: string): readonly string[] {
  if (value === '') return [];
  return decodeEntities(value)
    .split(/<\/?(?:p|br|div|li|ul|ol)[^>]*>|\r?\n/i)
    .map((line) =>
      line
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter((line) => line !== '');
}

function toShipToAddress(
  row: Record<string, unknown>,
  language: WarehouseLanguage,
): ShipToAddress | undefined {
  const id = str(row, 'id');
  if (id === '') return undefined;

  // `name` and `name_cn` carry the same value on every row observed; `name_cn` is read first and
  // `name` kept as the fallback, since the document names only the latter.
  const nameCn = firstNonEmpty(str(row, 'name_cn'), str(row, 'name'));
  const nameEn = str(row, 'name_en');
  // Falls back to the vendor's id for a row carrying no name in either language. Not a case the
  // live data has, but the alternative is a section with an empty heading, and showing the raw id
  // is what `PackageListRow` already does when it cannot resolve a warehouse — a visible
  // identifier someone can quote at the vendor beats a blank.
  const label = firstNonEmpty(...(language === 'zh' ? [nameCn, nameEn] : [nameEn, nameCn]), id);

  // Raw HTML, not decoded text: `str` would collapse the `\r\n` that separates the lines.
  const attention = row[language === 'zh' ? 'attention_cn' : 'attention_en'];
  const attentionFallback = row[language === 'zh' ? 'attention_en' : 'attention_cn'];
  const notices = toNotices(typeof attention === 'string' ? attention : '');

  return {
    id,
    country: COUNTRY_BY_ID[str(row, 'country')],
    label,
    // Only when it says something the label does not — in the Chinese UI the two are the
    // same string, and a heading repeated underneath itself reads like a rendering fault.
    labelNative: nameCn !== '' && nameCn !== label ? nameCn : undefined,
    recipient: str(row, 'contact'),
    addressLine: str(row, 'address'),
    postcode: str(row, 'zipcode'),
    phone: str(row, 'phone'),
    // `|| undefined` rather than an empty string, so the optional field is actually absent when
    // the vendor has no remark — three of the six warehouses don't — instead of being a declared
    // optional that is never once undefined.
    remark:
      firstNonEmpty(
        str(row, language === 'zh' ? 'remark_cn' : 'remark_en'),
        str(row, language === 'zh' ? 'remark_en' : 'remark_cn'),
        // Documented as `remark`; observed as a duplicate of `remark_cn`. Last, so it can only
        // ever fill a gap the two explicit fields left.
        str(row, 'remark'),
      ) || undefined,
    // Falling back across languages deliberately: these are customs, weight and destruction
    // rules, and a notice the reader can paste into a translator beats silence about one.
    notices:
      notices.length > 0
        ? notices
        : toNotices(typeof attentionFallback === 'string' ? attentionFallback : ''),
    freeStorageDays: days(row, 'expiredays'),
    destroyAfterDays: days(row, 'destroydays'),
  };
}

/**
 * Every warehouse a customer may forward to, in the order the endpoint returns them.
 *
 * Two flags decide what a customer sees. `show` is the vendor's own switch for this list — two of
 * the eight rows are sea-freight loading warehouses with `show: "N"`, and the legacy page omits
 * them, so honouring it is what makes this list match what customers are used to. `status` is the
 * record's active flag. A row failing either is dropped rather than greyed out: an address a
 * customer must not post to is not information, it is a mistake waiting to be copied.
 *
 * Takes `unknown[]` because that is what the envelope layer hands back, and a row that is not an
 * object — or has no id to key policy and package lookups by — is skipped rather than allowed to
 * throw partway through rendering a list.
 */
export function normaliseWarehouses(
  rows: readonly unknown[],
  language: WarehouseLanguage,
): readonly ShipToAddress[] {
  const visible: ShipToAddress[] = [];

  for (const row of rows) {
    if (!isRecord(row)) continue;
    if (str(row, 'show').toUpperCase() === 'N') continue;
    if (str(row, 'status').toUpperCase() === 'N') continue;

    const address = toShipToAddress(row, language);
    if (address) visible.push(address);
  }

  return visible;
}
